<script setup lang="ts">
import type { NuxtError } from '#app'
import { localeFromUrl } from '~/utils/routeMapping'

const props = defineProps<{ error: NuxtError }>()

const locale = computed(() => localeFromUrl(useRequestURL().pathname))

const is404 = computed(() => props.error.status === 404)

// 404 telemetry. Static-asset-miss 404s are served as prerendered /404.html by
// Cloudflare Pages at the edge, bypassing the Worker — this hydrated page (URL
// preserved) is the only place that sees them. Beacon the missed path so the
// NF_ANALYTICS dataset captures both static and SSR 404s. Fire-and-forget; any
// failure is swallowed so telemetry can never break the error page.
onMounted(() => {
  if (props.error.status !== 404 || typeof navigator?.sendBeacon !== 'function') return
  try {
    const payload = JSON.stringify({
      path: location.pathname + location.search,
      referrer: document.referrer || '',
    })
    navigator.sendBeacon('/api/monitor/404', new Blob([payload], { type: 'application/json' }))
  } catch {
    /* best-effort telemetry */
  }
})

function handleError() {
  clearError({ redirect: `/${locale.value}` })
}
</script>

<template>
  <div class="min-h-screen flex items-center justify-center bg-(--color-elevation-50)">
    <div class="text-center max-w-md px-6">
      <p class="text-6xl font-bold text-(--color-elevation-300) mb-4">
        {{ error.status }}
      </p>
      <h1 class="text-2xl font-bold text-(--color-elevation-900) mb-2">
        {{ is404 ? 'Page not found' : 'Something went wrong' }}
      </h1>
      <p class="text-(--color-elevation-500) mb-8">
        {{ is404
          ? 'The page you are looking for does not exist or has been moved.'
          : (error.statusText || 'An unexpected error occurred.')
        }}
      </p>
      <button
        class="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold bg-(--color-accent) text-white hover:opacity-90 transition-opacity"
        @click="handleError"
      >
        Go to homepage
      </button>
    </div>
  </div>
</template>
